import React, { useState } from 'react';
import { Handle, Position } from 'reactflow';
import { MessageCircle, CheckCircle, XCircle, Clock, ChevronLeft, ChevronRight, Eye, Copy, ExternalLink } from 'lucide-react';

const ResponseNode = ({ data, isConnectable, selected }) => {
  const [currentResponseIndex, setCurrentResponseIndex] = useState(0);
  
  // Parse execution results if available
  let executionResult = null;
  let executionStatus = null;
  
  if (data.executionResult) {
    try {
      executionResult = typeof data.executionResult === 'string' 
        ? JSON.parse(data.executionResult) 
        : data.executionResult;
      executionStatus = data.executionStatus || 'completed';

    } catch (e) {
      console.error('Error parsing execution result:', e);
    }
  } else {
  }

  const getStatusIcon = () => {
    switch (executionStatus) {
      case 'completed':
        return <CheckCircle className="h-3 w-3 text-celadon-600" />;
      case 'failed':
        return <XCircle className="h-3 w-3 text-gray-500" />;
      case 'running':
        return <Clock className="h-3 w-3 text-gray-500 animate-spin" />;
      default:
        return null;
    }
  };

  const getNodeBorderColor = () => {
    if (selected) {
      return 'border-celadon-400 shadow-celadon-200/50 shadow-lg';
    }
    
    switch (executionStatus) {
      case 'completed':
        return 'border-celadon-300 shadow-celadon-100/50';
      case 'failed':
        return 'border-gray-300 shadow-gray-100/50';
      case 'running':
        return 'border-gray-300 shadow-gray-100/50';
      default:
        return 'border-gray-200 shadow-gray-50/50';
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Extract responses from the connected node's execution result
  const getResponsesFromExecutionResult = () => {
    
    if (!executionResult) {
      return [];
    }

    // Helper function to extract the LLM response text from a result object
    const extractResponseText = (result, nodeType = 'unknown') => {
      // For auto-evaluator results, prioritize reasoning (the LLM response)
      if (result.reasoning) {
        return result.reasoning;
      }
      if (result.scoreReason) {
        return result.scoreReason;
      }
      
      // For prompt results, use response
      if (result.response) {
        return result.response;
      }
      if (result.output) {
        return result.output;
      }
      
      return 'No response available';
    };

    // Helper function to determine if this is evaluation data
    const isEvaluationResult = (result) => {
      return result.hasOwnProperty('score') || 
             result.hasOwnProperty('passed') || 
             result.hasOwnProperty('reasoning') ||
             result.hasOwnProperty('scoreReason');
    };

    // Helper function to map a result to our display format
    const mapResult = (result, index = 0) => {
      const isEval = isEvaluationResult(result);
      return {
        text: extractResponseText(result),
        input: result.input || 'No input',
        testCaseId: result.testCaseId,
        testCaseIndex: index,
        model: isEval ? 'Auto-Evaluator' : (result.model || 'Unknown'),
        tokens: result.totalTokens || result.tokens || 0,
        executionTime: result.executionTime || 0,
        score: result.score,
        passed: result.passed,
        reasoning: result.reasoning,
        originalResponse: result.response,
        isEvaluation: isEval
      };
    };

    // NEW: Check if this ResponseNode received input from connected nodes
    if (executionResult.inputs) {
      
      // Look through inputs to find connected node outputs
      for (const [inputKey, inputValue] of Object.entries(executionResult.inputs)) {
        
        // Check if this is a complete node result with output
        if (inputValue && inputValue.output) {
          const output = inputValue.output;
          
          // Handle multiple results (from test cases)
          if (output.results && Array.isArray(output.results)) {
            const mapped = output.results.map((result, index) => mapResult(result, index));
            return mapped;
          }
          
          // Handle single response
          if (output.response) {
            const mapped = [mapResult({
              response: output.response,
              model: output.model,
              totalTokens: output.totalTokens,
              executionTime: output.executionTime
            })];
            return mapped;
          }
        }
        
        // Check if this is a direct results array
        if (inputValue && Array.isArray(inputValue.results)) {
          const mapped = inputValue.results.map((result, index) => mapResult(result, index));
          return mapped;
        }
        
        // Check if this is a direct response
        if (inputValue && inputValue.response) {
          const mapped = [mapResult(inputValue)];
          return mapped;
        }
      }
    }

    // Fallback: Try to find data in the most common structures (original logic)
    let sourceData = null;

    // 1. Check direct output results
    if (executionResult.output?.results) {
      sourceData = executionResult.output.results;
    }
    // 2. Check for single response structures
    else if (executionResult.output?.response) {
      sourceData = [{ 
        response: executionResult.output.response, 
        model: executionResult.output.model 
      }];
    }

    if (sourceData && Array.isArray(sourceData)) {
      const mapped = sourceData.map((result, index) => mapResult(result, index));
      return mapped;
    }

    return [];
  };

  const responses = getResponsesFromExecutionResult();

  // Reset current index if it's out of bounds
  if (currentResponseIndex >= responses.length && responses.length > 0) {
    setCurrentResponseIndex(0);
  }

  const navigateResponse = (direction) => {
    if (direction === 'next') {
      setCurrentResponseIndex((prev) => (prev + 1) % responses.length);
    } else {
      setCurrentResponseIndex((prev) => (prev - 1 + responses.length) % responses.length);
    }
  };

  const formatResponseText = (responseText, displayMode) => {
    if (!responseText) return 'No response available';
    
    const maxLength = data.maxLength || 150; // Allow custom max length
    
    switch (displayMode) {
      case 'full':
        return responseText;
      case 'preview':
        return responseText.length > maxLength ? `${responseText.substring(0, maxLength)}...` : responseText;
      case 'summary':
        // For summary mode, show even less text
        const summaryLength = Math.min(100, maxLength);
        return responseText.length > summaryLength ? `${responseText.substring(0, summaryLength)}...` : responseText;
      default:
        // Default to preview mode
        return responseText.length > maxLength ? `${responseText.substring(0, maxLength)}...` : responseText;
    }
  };

  const renderResponseContent = () => {
    
    if (responses.length === 0) {
      return (
        <div className="text-center py-4 text-gray-500">
          <Eye className="h-6 w-6 mx-auto mb-2 text-gray-300" />
          <p className="text-xs">No response data available</p>
          <p className="text-xs text-gray-400 mt-1">Connect to a Prompt or Auto-Evaluator node</p>
          {process.env.NODE_ENV === 'development' && (
            <details className="mt-2 text-left">
              <summary className="text-xs cursor-pointer">Debug Info</summary>
              <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-auto max-h-20">
                {JSON.stringify({
                  hasExecutionResult: !!executionResult,
                  executionStatus,
                  dataKeys: Object.keys(data),
                  executionResultKeys: executionResult ? Object.keys(executionResult) : null,
                  executionResultInputs: executionResult?.inputs ? Object.keys(executionResult.inputs) : null
                }, null, 2)}
              </pre>
            </details>
          )}
        </div>
      );
    }

    const displayMode = data.displayMode || 'preview';
    const currentResponse = responses[currentResponseIndex] || responses[0];
    const responseText = currentResponse.text || currentResponse.response || 'No response available';
    const formattedText = formatResponseText(responseText, displayMode);

    return (
      <div className="space-y-3">
        {/* Multiple responses navigation */}
        {responses.length > 1 && (
          <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
            <button
              onClick={() => navigateResponse('prev')}
              className="flex items-center space-x-1 text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50"
              disabled={responses.length <= 1}
            >
              <ChevronLeft className="h-3 w-3" />
              <span>Previous</span>
            </button>
            
            <span className="text-xs font-medium text-gray-700">
              {currentResponseIndex + 1} of {responses.length}
            </span>
            
            <button
              onClick={() => navigateResponse('next')}
              className="flex items-center space-x-1 text-xs text-gray-600 hover:text-gray-800 disabled:opacity-50"
              disabled={responses.length <= 1}
            >
              <span>Next</span>
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* Response content */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-700">
              {currentResponse.isEvaluation 
                ? (responses.length > 1 ? `Evaluation ${currentResponseIndex + 1}` : 'Evaluation Reasoning')
                : (responses.length > 1 ? `Response ${currentResponseIndex + 1}` : 'Response')
              }
            </span>
            <button
              onClick={() => copyToClipboard(responseText)}
              className="text-gray-400 hover:text-gray-600"
              title={currentResponse.isEvaluation ? "Copy evaluation reasoning" : "Copy response"}
            >
              <Copy className="h-3 w-3" />
            </button>
          </div>
          
          <div className="bg-gray-50 p-3 rounded text-xs">
            <p className="text-gray-600 break-words whitespace-pre-wrap">
              {formattedText}
            </p>
          </div>
          
          {/* Response metadata */}
          {(currentResponse.model || currentResponse.tokens || currentResponse.executionTime || currentResponse.isEvaluation) && (
            <div className="space-y-1 pt-1">
              <div className="flex items-center justify-between text-xs text-gray-500">
                {currentResponse.model && (
                  <span>{currentResponse.model.split('-')[0]}</span>
                )}
                <div className="flex items-center space-x-2">
                  {currentResponse.tokens && (
                    <span>{currentResponse.tokens} tokens</span>
                  )}
                  {currentResponse.executionTime && (
                    <span>{currentResponse.executionTime}ms</span>
                  )}
                </div>
              </div>
              
              {/* Evaluation-specific metadata */}
              {currentResponse.isEvaluation && (
                <div className="flex items-center justify-between text-xs bg-gray-100 px-2 py-1 rounded">
                  {currentResponse.score !== undefined && (
                    <span className={`font-medium ${currentResponse.passed ? 'text-celadon-600' : 'text-gray-600'}`}>
                      Score: {Math.round(currentResponse.score * 100)}%
                    </span>
                  )}
                  {currentResponse.passed !== undefined && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      currentResponse.passed 
                        ? 'bg-celadon-100 text-celadon-700' 
                        : 'bg-gray-200 text-gray-700'
                    }`}>
                      {currentResponse.passed ? 'Passed' : 'Failed'}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`bg-white rounded-xl border-2 ${getNodeBorderColor()} shadow-soft transition-all duration-200 min-w-[240px]`}>
      <Handle
        type="target"
        position={Position.Left}
        isConnectable={isConnectable}
        className="w-3 h-3 !bg-celadon-400 !border-2 !border-white"
      />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-100 to-gray-50 px-4 py-3 rounded-t-2xl border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="p-1.5 bg-gray-100 rounded-lg">
              <MessageCircle className="h-4 w-4 text-gray-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{data.label || 'Response Display'}</h3>
              <p className="text-xs text-gray-600">LLM Response Viewer</p>
            </div>
          </div>
          {getStatusIcon()}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-3 space-y-3">
        {/* Configuration Display */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-700">Display Mode:</span>
            <span className="text-xs text-gray-600">
              {data.displayMode === 'full' ? 'Full Text' : 
               data.displayMode === 'summary' ? 'Summary' : 'Preview'}
            </span>
          </div>
          {data.maxLength && data.displayMode !== 'full' && (
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-700">Max Length:</span>
              <span className="text-xs text-gray-600">{data.maxLength} chars</span>
            </div>
          )}
        </div>

        {/* Response Content */}
        {renderResponseContent()}

        {/* Execution Summary */}
        {executionResult && responses.length > 0 && (
          <div className="pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">
                {responses.length > 1 
                  ? `${responses.length} responses`
                  : 'Single response'
                }
                {responses.some(r => r.isEvaluation) && (
                  <span className="ml-1 text-celadon-600">
                    ({responses.filter(r => r.isEvaluation).length} evaluations)
                  </span>
                )}
              </span>
              <span className="text-gray-500">
                {data.displayMode === 'full' ? 'Full' : 
                 data.displayMode === 'summary' ? 'Summary' : 'Preview'}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResponseNode; 